import type { Price } from '@prisma/client'

/**
 * Enumerates subscription plan names.
 * These are used as unique identifiers in both the database and Stripe dashboard.
 */
export const PLANS = {
  FREE: 'free',
  STARTER: 'starter',
  PRO: 'pro',
  BUSINESS: 'business',
} as const

export type Plan = (typeof PLANS)[keyof typeof PLANS]

/**
 * Enumerates billing intervals for subscription plans.
 */
export const INTERVALS = {
  MONTH: 'month',
  YEAR: 'year',
} as const

export type Interval = (typeof INTERVALS)[keyof typeof INTERVALS]

/**
 * Enumerates supported currencies for billing.
 */
export const CURRENCIES = {
  DEFAULT: 'usd',
  USD: 'usd',
  EUR: 'eur',
} as const

export type Currency = (typeof CURRENCIES)[keyof typeof CURRENCIES]

/**
 * Defines the structure for each subscription plan.
 *
 * Note:
 * - Running the Prisma seed will create these plans in your Stripe Dashboard and populate the database.
 * - Each plan includes pricing details for each interval and currency.
 * - Plan IDs correspond to the Stripe plan IDs for easy identification.
 * - 'name' and 'description' fields are used in Stripe Checkout and client UI.
 */
export const PRICING_PLANS = {
  [PLANS.FREE]: {
    id: PLANS.FREE,
    name: 'Free',
    description: 'Start with the basics, upgrade anytime.',
    prices: {
      [INTERVALS.MONTH]: {
        [CURRENCIES.USD]: 0,
        [CURRENCIES.EUR]: 0,
      },
      [INTERVALS.YEAR]: {
        [CURRENCIES.USD]: 0,
        [CURRENCIES.EUR]: 0,
      },
    },
  },
  [PLANS.STARTER]: {
    id: PLANS.STARTER,
    name: 'Starter',
    description: 'For individuals getting started with email automation.',
    prices: {
      [INTERVALS.MONTH]: {
        [CURRENCIES.USD]: 1400,
        [CURRENCIES.EUR]: 1400,
      },
      [INTERVALS.YEAR]: {
        [CURRENCIES.USD]: 13440,
        [CURRENCIES.EUR]: 13440,
      },
    },
  },
  [PLANS.PRO]: {
    id: PLANS.PRO,
    name: 'Pro',
    description: 'Full observability and advanced configuration.',
    prices: {
      [INTERVALS.MONTH]: {
        [CURRENCIES.USD]: 2900,
        [CURRENCIES.EUR]: 2900,
      },
      [INTERVALS.YEAR]: {
        [CURRENCIES.USD]: 27840,
        [CURRENCIES.EUR]: 27840,
      },
    },
  },
  [PLANS.BUSINESS]: {
    id: PLANS.BUSINESS,
    name: 'Business',
    description: 'For teams that need scale and advanced debugging.',
    prices: {
      [INTERVALS.MONTH]: {
        [CURRENCIES.USD]: 9900,
        [CURRENCIES.EUR]: 9900,
      },
      [INTERVALS.YEAR]: {
        [CURRENCIES.USD]: 95040,
        [CURRENCIES.EUR]: 95040,
      },
    },
  },
} satisfies PricingPlan

/**
 * Defines numeric limits and feature flags for each plan.
 * Used by the seed to populate the database and by client code for display.
 */
export const PLAN_LIMITS: Record<
  Plan,
  {
    maxBridges: number
    maxMonthlyEmails: number
    maxRetryAttempts: number
    retentionDays: number
    hasManualReplay: boolean
    hasJsonViewer: boolean
    hasCustomHeaders: boolean
    hasFullObservability: boolean
    hasAdvancedDebugging: boolean
    hasPrioritySupport: boolean
    hasAttachments: boolean
  }
> = {
  [PLANS.FREE]: {
    maxBridges: 1,
    maxMonthlyEmails: 1000,
    maxRetryAttempts: 3,
    retentionDays: 7,

    hasManualReplay: false,
    hasJsonViewer: false,
    hasCustomHeaders: false,
    hasFullObservability: false,
    hasAdvancedDebugging: false,
    hasPrioritySupport: false,
    hasAttachments: false,
  },
  [PLANS.STARTER]: {
    maxBridges: 5,
    maxMonthlyEmails: 5000,
    maxRetryAttempts: 5,
    retentionDays: 30,

    hasManualReplay: true,
    hasJsonViewer: true,
    hasCustomHeaders: false,
    hasFullObservability: false,
    hasAdvancedDebugging: false,
    hasPrioritySupport: false,
    hasAttachments: true,
  },
  [PLANS.PRO]: {
    maxBridges: 15,
    maxMonthlyEmails: 10000,
    maxRetryAttempts: 10,
    retentionDays: 90,

    hasManualReplay: true,
    hasJsonViewer: true,
    hasCustomHeaders: true,
    hasFullObservability: true,
    hasAdvancedDebugging: false,
    hasPrioritySupport: true,
    hasAttachments: true,
  },
  [PLANS.BUSINESS]: {
    maxBridges: 50,
    maxMonthlyEmails: 50000,
    maxRetryAttempts: 15,
    retentionDays: 180,

    hasManualReplay: true,
    hasJsonViewer: true,
    hasCustomHeaders: true,
    hasFullObservability: true,
    hasAdvancedDebugging: true,
    hasPrioritySupport: true,
    hasAttachments: true,
  },
}

/**
 * Ordered list of plan IDs from lowest to highest tier.
 * Used to determine upgrade vs downgrade direction.
 */
export const PLAN_ORDER: Plan[] = [PLANS.FREE, PLANS.STARTER, PLANS.PRO, PLANS.BUSINESS]

/**
 * Returns the monthly price in whole-dollar USD for the given plan and interval.
 * For yearly, this is the effective monthly cost (yearly total / 12).
 */
export function getPlanPriceUsd(
  planId: Plan,
  interval: Interval,
): number {
  const cents = PRICING_PLANS[planId].prices[interval][CURRENCIES.USD]
  return interval === INTERVALS.YEAR ? cents / 12 / 100 : cents / 100
}

/**
 * A type helper defining prices for each billing interval and currency.
 */
type PriceInterval<I extends Interval = Interval, C extends Currency = Currency> = {
  [interval in I]: {
    [currency in C]: Price['amount']
  }
}

/**
 * A type helper defining the structure for subscription pricing plans.
 */
type PricingPlan<T extends Plan = Plan> = {
  [key in T]: {
    id: string
    name: string
    description: string
    prices: PriceInterval
  }
}
